import { apiSlice } from "./index";
import { Link as RLink } from "react-router-dom";
import Link from "@mui/material/Link";
import {
    upsertTicket,
    upsertTickets,
    upsertTicketItems,
    upsertTicketItemsPeriod,
    childLevels,
    Ticket,
    TicketEditRequest,
    TicketItem,
    ShiftPlannedStartsRequest,
} from "../features/ticket/ticketSlice";

export interface TicketResponse {
    ticket: Ticket | null;
    ticket_items: { [key: string]: TicketItem };
    errors: string[];
}

export interface TicketItemsResponse {
    tickets: { [key: string]: Ticket };
    ticket_items: { [key: string]: TicketItem };
    errors: string[];
}

type Levels = {
    level_1: boolean;
    level_2: boolean;
    level_3: boolean;
    level_4: boolean;
    level_5: boolean;
    level_6: boolean;
};

export function levelsToQuery(ktype: string): string {
    let levels = childLevels(ktype);
    const params = Object.entries(levels)
        .filter(([, value]) => value)
        .map(([key]) => `${encodeURIComponent(key)}=true`);

    return params.length ? `?${params.join("&")}` : "";
}

const ticketApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        ticketView: builder.query({
            //query: (id) => `ticket/items-view/${id}?planned_start_after=2024-08-15T12:00:00Z`,
            query: (id) => `ticket/view/${id}`,
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                console.log("ticket query started");
                try {
                    const { data } = await queryFulfilled;
                    console.log("Data Received", data);
                    dispatch(upsertTicketItems(data.ticket_items));
                    dispatch(upsertTicket(data.ticket));
                } catch (err) {
                    console.log("ho no, error in ticket query");
                }
            },
        }),
        ticketItems: builder.query({
            //query: (id) => `ticket/items-view/${id}?planned_start_after=2024-08-15T12:00:00Z`,
            query: (id) => `ticket/items-view/${id}`,
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                console.log("ticket query started");
                try {
                    const { data } = await queryFulfilled;
                    console.log("Data Received", data);
                    dispatch(upsertTicketItems(data.ticket_items));
                } catch (err) {
                    console.log("ho no, error in ticket query");
                }
            },
        }),
        ticketByPeriod: builder.query<
            TicketItemsResponse,
            { min: string; max: string }
        >({
            query: ({ min, max }) =>
                `ticket/items-view/?planned_start_min=${min}&planned_start_max=${max}&ktype=6`,
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                console.log("ticket query started", args.min, args.max);
                try {
                    const { data } = await queryFulfilled;
                    console.log("Data Received", data);
                    dispatch(
                        upsertTicketItemsPeriod({
                            items: data.ticket_items,
                            min: args.min,
                            max: args.max,
                        }),
                    );
                } catch (err) {
                    console.log("ho no, error in ticket query");
                }
            },
        }),
        ticketByContext: builder.query<
            TicketItemsResponse,
            { context_id: string | null; end_min: string }
        >({
            query: ({ context_id, end_min }) =>
                `ticket/items-view/?context_id=${context_id}&end_min=${end_min}&end_is_null=true&ktype=6`,
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                console.log("ticket query started");
                try {
                    const { data } = await queryFulfilled;
                    console.log("Data Received", data);
                    dispatch(upsertTicketItems(data.ticket_items));
                } catch (err) {
                    console.log("ho no, error in ticket query");
                }
            },
        }),
        ticketByDelTag: builder.query<
            TicketItemsResponse,
            { tag: string | null; end_min: string }
        >({
            query: ({ tag, end_min }) =>
                `ticket/items-view/?tag=${tag}&end_min=${end_min}&end_is_null=true&level_5=true`,
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                console.log("ticket query started");
                try {
                    const { data } = await queryFulfilled;
                    console.log("Data Received", data);
                    dispatch(upsertTicketItems(data.ticket_items));
                } catch (err) {
                    console.log("ho no, error in ticket query");
                }
            },
        }),
        ticketParent: builder.query<
            TicketItemsResponse,
            { ktype: string; inputValue: string }
        >({
            query: ({ ktype, inputValue }) =>
                `ticket/items-view/?${levelsToQuery(ktype)}&text=${inputValue}&end_is_null=true`,
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                console.log("ticket query started");
                try {
                    const { data } = await queryFulfilled;
                    console.log("Data Received", data);
                    dispatch(upsertTicketItems(data.ticket_items));
                } catch (err) {
                    console.log("ho no, error in ticket query");
                }
            },
        }),
        shiftPlannedStarts: builder.mutation<
            TicketItemsResponse,
            ShiftPlannedStartsRequest
        >({
            query: (body) => {
                return {
                    url: "/ticket/shift-planned-start/",
                    method: "POST",
                    body,
                };
            },
            invalidatesTags: [],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                console.log("202410272311 shift api", arg);
                try {
                    const { data } = await queryFulfilled;
                    dispatch(upsertTicketItems(data.ticket_items));
                } catch (err) {
                    console.log("shiftPlannedStarts ajax:", err);
                }
            },
        }),
        edit: builder.mutation<TicketResponse, TicketEditRequest>({
            query: (body) => {
                const idStr = body.id ? "/" + body.id : "";
                return {
                    url: "/ticket/edit" + idStr,
                    method: "POST",
                    body,
                };
            },
            invalidatesTags: [],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    if (data.ticket) {
                        dispatch(upsertTicket(data.ticket));
                    }
                } catch (err) {
                    console.log("202411301427: edit ticket api error", err);
                }
            },
        }),
        copy: builder.mutation<TicketResponse, string>({
            query: (id) => {
                const idStr = "/" + id;
                return {
                    url: "/ticket/copy" + idStr,
                    method: "POST",
                };
            },
            invalidatesTags: [],
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    if (data.ticket) {
                        dispatch(upsertTicket(data.ticket));
                    }
                } catch (err) {
                    console.log("202501031130: copy ticket api error", err);
                }
            },
        }),
    }),
});

export const {
    useTicketViewQuery,
    useTicketItemsQuery,
    useTicketByPeriodQuery,
    useTicketByContextQuery,
    useTicketByDelTagQuery,
    useTicketParentQuery,
    useShiftPlannedStartsMutation,
    useEditMutation,
    useCopyMutation,
} = ticketApi;
